import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { EVENTS_SCHEDULE, SERVER_TIMEZONE_OFFSET, DAY_NAMES, DAY_NAMES_SHORT } from '../data/eventsSchedule';

// Get current time in server timezone (GMT+3)
function getServerTime() {
  const now = new Date();
  // Get UTC time then add server offset
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc + (SERVER_TIMEZONE_OFFSET * 3600000));
}

// Get user's timezone offset in hours (e.g., +2, -5)
function getUserTimezoneOffset() {
  return -new Date().getTimezoneOffset() / 60;
}

// Format timezone offset for display (e.g., "+3", "-5", "+5:30")
function formatTimezoneOffset(offset) {
  const sign = offset >= 0 ? '+' : '';
  const hours = Math.floor(Math.abs(offset));
  const minutes = Math.abs(offset % 1) * 60;
  if (minutes > 0) {
    return `${sign}${offset >= 0 ? hours : -hours}:${minutes.toString().padStart(2, '0')}`;
  }
  return `${sign}${offset}`;
}

// Parse time string "HH:MM" to minutes since midnight
function parseTimeToMinutes(timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

// Format minutes to "HH:MM"
function formatMinutesToTime(minutes) {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

// Convert server time to user's local time
function serverTimeToLocal(serverTimeStr, serverDay) {
  const serverMinutes = parseTimeToMinutes(serverTimeStr);
  const userOffset = getUserTimezoneOffset();
  const offsetDiff = userOffset - SERVER_TIMEZONE_OFFSET; // difference in hours

  let localMinutes = serverMinutes + (offsetDiff * 60);
  let dayDiff = 0;

  if (localMinutes < 0) {
    localMinutes += 24 * 60;
    dayDiff = -1;
  } else if (localMinutes >= 24 * 60) {
    localMinutes -= 24 * 60;
    dayDiff = 1;
  }

  const localDay = (serverDay + dayDiff + 7) % 7;

  return {
    time: formatMinutesToTime(localMinutes),
    day: localDay,
    dayDiff
  };
}

// Get scheduled events for display
function getEventsForDisplay(serverTime, lang) {
  const serverDay = serverTime.getDay();
  const serverHours = serverTime.getHours();
  const serverMinutes = serverTime.getMinutes();
  const currentMinutes = serverHours * 60 + serverMinutes;

  const todaySchedule = EVENTS_SCHEDULE[serverDay] || [];
  const nextDay = (serverDay + 1) % 7;
  const nextDaySchedule = EVENTS_SCHEDULE[nextDay] || [];

  // Find events before current time (for "past" section)
  const pastEvents = [];
  const futureEvents = [];

  todaySchedule.forEach((event, index) => {
    const eventMinutes = parseTimeToMinutes(event.time);
    const localInfo = serverTimeToLocal(event.time, serverDay);

    const eventData = {
      ...event,
      serverTime: event.time,
      serverDay,
      localTime: localInfo.time,
      localDay: localInfo.day,
      localDayDiff: localInfo.dayDiff,
      minutes: eventMinutes,
      id: `today-${index}`
    };

    if (eventMinutes <= currentMinutes) {
      pastEvents.push(eventData);
    } else {
      futureEvents.push(eventData);
    }
  });

  // Get last 3 past events
  const recentPastEvents = pastEvents.slice(-3);

  // Get all future events for today
  const todayFutureEvents = futureEvents;

  // Get first 10 events from next day
  const nextDayEvents = nextDaySchedule.slice(0, 10).map((event, index) => {
    const localInfo = serverTimeToLocal(event.time, nextDay);
    return {
      ...event,
      serverTime: event.time,
      serverDay: nextDay,
      localTime: localInfo.time,
      localDay: localInfo.day,
      localDayDiff: localInfo.dayDiff,
      minutes: parseTimeToMinutes(event.time),
      id: `next-${index}`,
      isNextDay: true
    };
  });

  return {
    pastEvents: recentPastEvents,
    todayFutureEvents,
    nextDayEvents,
    serverDay,
    nextDay,
    currentMinutes
  };
}

export function EventsSchedule() {
  const { t, lang } = useLanguage();
  const [currentTime, setCurrentTime] = useState(getServerTime());
  const [userTimezone] = useState(getUserTimezoneOffset());

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getServerTime());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const eventsData = useMemo(() => {
    return getEventsForDisplay(currentTime, lang);
  }, [currentTime, lang]);

  const dayNames = DAY_NAMES[lang] || DAY_NAMES['en'];
  const dayNamesShort = DAY_NAMES_SHORT[lang] || DAY_NAMES_SHORT['en'];

  const serverTimeStr = `${currentTime.getHours().toString().padStart(2, '0')}:${currentTime.getMinutes().toString().padStart(2, '0')}`;
  const localTime = new Date();
  const localTimeStr = `${localTime.getHours().toString().padStart(2, '0')}:${localTime.getMinutes().toString().padStart(2, '0')}`;

  const renderEventRow = (event, isPast = false) => {
    const showLocalDayIndicator = event.localDayDiff !== 0;

    return (
      <div
        key={event.id}
        className={`event-row ${isPast ? 'event-past' : ''}`}
      >
        <div className="event-times">
          <span className="event-local-time">
            {event.localTime}
            {showLocalDayIndicator && (
              <span className="event-day-indicator">
                {event.localDayDiff > 0 ? '+1' : '-1'}
              </span>
            )}
          </span>
          <span className="event-server-time">{event.serverTime}</span>
        </div>
        <div className="event-name">{event.event}</div>
      </div>
    );
  };

  return (
    <div className="events-schedule">
      {/* Events Header */}
      <div className="events-list-header">
        <span className="events-header-local">{t('eventsYourTime')}</span>
        <span className="events-header-server">{t('eventsServerTimeHeader')}</span>
        <span className="events-header-event">{t('eventsEventName')}</span>
      </div>

      {/* Past Events */}
      {eventsData.pastEvents.length > 0 && (
        <div className="events-section events-past-section">
          <div className="events-section-label">{t('eventsRecentPast')}</div>
          {eventsData.pastEvents.map(event => renderEventRow(event, true))}
        </div>
      )}

      {/* Current separator */}
      <div className="events-now-separator">
        <span className="events-now-line"></span>
        <span className="events-now-label">{t('eventsNow')}</span>
        <span className="events-now-line"></span>
      </div>

      {/* Today's Future Events */}
      {eventsData.todayFutureEvents.length > 0 && (
        <div className="events-section events-today-section">
          <div className="events-section-label">
            {t('eventsToday')} - {dayNamesShort[eventsData.serverDay]}
          </div>
          {eventsData.todayFutureEvents.map(event => renderEventRow(event))}
        </div>
      )}

      {/* Next Day Events */}
      {eventsData.nextDayEvents.length > 0 && (
        <div className="events-section events-nextday-section">
          <div className="events-section-label">
            {t('eventsTomorrow')} - {dayNamesShort[eventsData.nextDay]}
          </div>
          {eventsData.nextDayEvents.map(event => renderEventRow(event))}
        </div>
      )}

      <p className="events-note">{t('eventsNote')}</p>

      {/* Time Display */}
      <div className="events-time-display">
        <div className="time-block local-time">
          <span className="time-label">{t('eventsLocalTime')} (GMT{formatTimezoneOffset(userTimezone)})</span>
          <span className="time-value">{localTimeStr}</span>
          <span className="time-day">{dayNames[localTime.getDay()]}</span>
        </div>
        <div className="time-block server-time">
          <span className="time-label">{t('eventsServerTime')} (GMT{formatTimezoneOffset(SERVER_TIMEZONE_OFFSET)})</span>
          <span className="time-value">{serverTimeStr}</span>
          <span className="time-day">{dayNames[eventsData.serverDay]}</span>
        </div>
      </div>
    </div>
  );
}
